import { cn } from '@shared/lib/cn';
import { haptics } from '@shared/lib/haptics';

/**
 * Step indicator for multi-step flows — a segment per step, filled as each one
 * is reached, above a caption giving the position and the current step's name.
 *
 * Segments rather than dots-and-labels: per-step labels collide below ~4 steps
 * at 375px, and naming only the current step leaves room for the count.
 *
 * `tone="inverse"` draws it on dark chrome (the onboarding runner's pinned
 * header); the default suits light surfaces such as form cards.
 */
interface StepperProps {
  total: number;
  current: number;
  /** Step names — used for the caption and each segment's accessible label. */
  labels?: string[];
  /** `inverse` for dark backgrounds; default for light ones. */
  tone?: 'default' | 'inverse';
  className?: string;
  onStepSelect?: (index: number) => void;
  isStepAccessible?: (index: number) => boolean;
}

export function Stepper({
  total,
  current,
  labels,
  tone = 'default',
  className,
  onStepSelect,
  isStepAccessible,
}: StepperProps): JSX.Element {
  const safeTotal = Math.max(total, 1);
  const safeCurrent = Math.min(Math.max(current, 0), safeTotal - 1);
  const inverse = tone === 'inverse';
  const nameOf = (index: number) => labels?.[index] ?? `Step ${index + 1}`;

  // Filled means "reached", so step 1 of 3 already shows a third of the track —
  // a node-based fill would read as no progress at all on the first step.
  const segmentClass = (index: number) =>
    cn(
      'block h-[3px] rounded-pill transition-colors duration-normal',
      index <= safeCurrent
        ? inverse
          ? 'bg-white'
          : 'bg-primary-600'
        : inverse
          ? 'bg-white/25'
          : 'bg-hairline',
    );

  return (
    <div
      className={cn('flex flex-col gap-2.5', className)}
      role="progressbar"
      aria-valuenow={safeCurrent + 1}
      aria-valuemin={1}
      aria-valuemax={safeTotal}
    >
      <div className="flex gap-1.5">
        {Array.from({ length: safeTotal }).map((_, index) => {
          const selectable = Boolean(onStepSelect && isStepAccessible?.(index));

          // A 3px bar is far too thin to tap. Where steps are selectable the
          // segment grows a padded hit area and pulls it back out of the layout,
          // so the track is drawn identically whether or not it can be tapped.
          return selectable ? (
            <button
              key={index}
              type="button"
              onClick={() => {
                if (index !== safeCurrent) haptics.tap();
                onStepSelect?.(index);
              }}
              aria-current={index === safeCurrent ? 'step' : undefined}
              aria-label={nameOf(index)}
              className="-my-3 flex flex-1 cursor-pointer items-center py-3"
            >
              <span className={cn(segmentClass(index), 'w-full')} />
            </button>
          ) : (
            <span key={index} className={cn(segmentClass(index), 'flex-1')} aria-hidden />
          );
        })}
      </div>

      <div className="flex items-baseline justify-between gap-3">
        <span
          className={cn(
            'whitespace-nowrap text-[0.8125rem] font-semibold leading-4',
            inverse ? 'text-white/75' : 'text-ink-secondary',
          )}
        >
          Step {safeCurrent + 1} of {safeTotal}
        </span>
        <span
          className={cn(
            'truncate text-caption font-semibold',
            inverse ? 'text-white/90' : 'text-ink-secondary',
          )}
        >
          {nameOf(safeCurrent)}
        </span>
      </div>
    </div>
  );
}
